import { useMemo, useRef, useState, useEffect } from "react";
import { Tags, Check, Tag as TagIcon, Search } from "lucide-react";
import { cn } from "../../../../utils/utils";
import { Button } from "../../../../components/UI/Button";
import { Badge } from "../../../../components/UI/Bagde";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../../../../components/UI/Popover";

const TagAssignmentPopover = ({ selectedChat, tags, onTagsChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef(null);

  const selectedIds = selectedChat?.tagIds || [];
  const selectedCount = useMemo(
    () => tags.filter((t) => selectedIds.includes(t._id)).length,
    [tags, selectedIds]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tags;
    return tags.filter((t) => (t.name || "").toLowerCase().includes(q));
  }, [tags, query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${activeIndex}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const handleKeyDown = (e) => {
    if (filtered.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % filtered.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + filtered.length) % filtered.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      onTagsChange(filtered[activeIndex]);
    }
  };

  const handleClearAll = () => {
    tags
      .filter((t) => selectedIds.includes(t._id))
      .forEach((t) => onTagsChange(t));
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-md h-7 w-7"
          title="Gắn nhãn"
          aria-label="Gắn nhãn"
        >
          <TagIcon size={16} />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-72 p-0 overflow-hidden"
        align="start"
        alignOffset={-100}
        sideOffset={5}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          const input = e.currentTarget.querySelector("input[data-tag-search]");
          if (input) input.focus();
        }}
      >
        <div className="flex items-center justify-between px-3 pt-3 pb-2">
          <div className="text-sm font-medium flex items-center gap-1">
            <Tags size={16} />
            <span>Gắn nhãn hội thoại</span>
          </div>
          {selectedCount > 0 && (
            <Badge variant="secondary">{selectedCount} được chọn</Badge>
          )}
        </div>

        {tags.length > 0 && (
          <div className="px-3 pb-2">
            <div className="relative">
              <Search
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                data-tag-search
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Tìm nhãn…"
                className="w-full h-8 pl-8 pr-2 text-sm rounded-md border border-gray-200 focus:outline-none focus:border-gray-400"
              />
            </div>
          </div>
        )}

        <ul ref={listRef} className="max-h-64 overflow-y-auto pb-1">
          {tags.length === 0 ? (
            <li className="px-3 py-6 text-center text-xs text-gray-400">
              Shop chưa có nhãn nào
            </li>
          ) : filtered.length === 0 ? (
            <li className="px-3 py-6 text-center text-xs text-gray-400">
              Không tìm thấy nhãn khớp
            </li>
          ) : (
            filtered.map((tag, idx) => {
              const isSelected = selectedIds.includes(tag._id);
              const isActive = idx === activeIndex;
              return (
                <li key={tag._id} data-idx={idx}>
                  <button
                    type="button"
                    onClick={() => onTagsChange(tag)}
                    onMouseEnter={() => setActiveIndex(idx)}
                    title={tag.name}
                    className={cn(
                      "w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors",
                      isSelected ? "bg-secondary" : "hover:bg-gray-50",
                      isActive && "ring-2 ring-primary/40 ring-inset"
                    )}
                  >
                    <span
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: tag.color }}
                    />
                    <span className="flex-1 truncate">{tag.name}</span>
                    {isSelected && (
                      <Check
                        size={16}
                        className="text-primary flex-shrink-0"
                      />
                    )}
                  </button>
                </li>
              );
            })
          )}
        </ul>

        {selectedCount > 0 && (
          <div className="border-t border-gray-100">
            <button
              type="button"
              onClick={handleClearAll}
              className="w-full px-3 py-2 text-sm text-left text-red-600 hover:bg-red-50"
            >
              Bỏ tất cả nhãn ({selectedCount})
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default TagAssignmentPopover;
